/**
 * THE CHART, COMPACT (D-043) — the fourth shape of the card at the top of
 * Home, and the one built to a layout target instead of a screenshot: about
 * 340 CSS px for the whole card at normal text, a plot about 128px tall, and
 * more of the survey list on the first screen. Its history — round 5's card
 * with the total at left (D-035), round 5.1's rate-of-change caption and
 * trend arrow (D-036), the arrow grown to 32px (D-038 postscript) — is in
 * `docs/DECISIONS.md`; what survives of each is named below.
 *
 * ── The shape ───────────────────────────────────────────────────────────────
 * Title and the Monthly/Yearly toggle on one row. Under it, two columns: the
 * device's all-time total ("Total on device") at left, and at right this
 * period's count, its change, and what the change is against ("vs Aug 1–6"),
 * split by a hairline. Then the year (or span of years) at left and
 * "Through Sep 6" at right, over a full-width plot: navy bars, the current
 * period amber with its axis label bold, values printed over the bars.
 *
 * ── The total is the device's count, full stop (D-035 Decision #3, kept) ───
 * `records.length`, Monthly or Yearly alike; the toggle only changes how the
 * SAME records are bucketed for the bars and which comparison is read.
 *
 * ── The reading is a variance, always (D-043 postscript) ────────────────────
 * `periodChange` (src/ui/state/surveyList.ts) compares this calendar month
 * against all of last month, and this year against all of last year, as a
 * percent — her ruling. The first cut compared the same days of last month
 * and said the counts when there was nothing to divide by ("+20 from 0"),
 * which read as a second count under "20 this month"; now an empty previous
 * period reads +100% and the line under it names the base: "vs Aug (0)".
 *
 * ── Text never bleeds, overlaps, or gets clipped ─────────────────────────────
 * The brief forbids the usual escapes — a smaller font, `overflow: hidden`,
 * a shrunken card — so a tight fit changes the LAYOUT instead, decided from
 * rendered widths (`chartLayout.ts`) after every layout pass:
 *
 *   - axis labels: every month, every other month, every third — the stride
 *     that fits the widest label; the current month's label always prints;
 *   - value annotations: printed when every count fits over its own bar,
 *     otherwise none of them, and a "Show counts" disclosure (a real 44px
 *     button) lists every period with its exact count instead;
 *   - the summary row: two columns while the total and the change reading
 *     fit theirs, stacked when either would not (a 200% text setting).
 *
 * Widths are measured with a canvas, not read back from the DOM — a label
 * that has been allowed to wrap in the stacked layout would otherwise
 * measure narrower, un-stack, wrap again, and never settle.
 *
 * Every size in the card is a multiple of `--chart-rem` (see `.chart` in
 * theme.css), the card's own 16px unit — not the app's 18px root: doubling
 * that one number is how the E2E proves the 200% case, and a phone's own
 * text setting scales px text on both engines.
 *
 * Under the typography round's standard (D-042) the 16px runs — the axis
 * labels, the counts over the bars, the comparison dates, "Through Sep 6" —
 * are `meta`: secondary metadata beside a thing, never text needed to finish
 * a task. Everything else in the card is 18px or larger.
 *
 * ── The bars are not buttons (D-035 Decision #4, kept) ─────────────────────
 * Twelve bars across a phone leave about 22px each — no touch target. Each
 * `.chart__slot` is an `img` with its own `aria-label` naming the period and
 * its count, so nothing is hidden from assistive tech; the only interactive
 * surfaces are the toggle and, when values are relocated, the disclosure.
 *
 * ── Zero and future periods draw nothing ────────────────────────────────────
 * A count of 0 has no fill at all — not a 2px hairline pretending to be a
 * bar — and no value over it. The slot still holds its place on the axis.
 *
 * ── Motion ───────────────────────────────────────────────────────────────────
 * Fills rise from `scaleY: 0`, staggered, as every entrance in this app does;
 * `gsap.set` under `prefers-reduced-motion`. `key={range}` on `.chart__bars`
 * remounts the bars on every toggle flip so the rise plays for the new set.
 */

import gsap from 'gsap';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import type { StoredSurvey } from '../../db/store';
import { summaryStacks, valuesFit, visibleTicks } from '../state/chartLayout';
import { periodChange, surveysByMonth, surveysByYear, type ChartRange } from '../state/surveyList';
import { Segmented } from './Segmented';

export interface SurveyChartProps {
  records: readonly StoredSurvey[];
}

const RANGE_OPTIONS: { value: ChartRange; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

/** "1,284" — every count this card prints goes through here. */
const NUMBER = new Intl.NumberFormat('en-US');

/** "1 survey" / "2 surveys" — every accessible count goes through here. */
function countWord(count: number): string {
  return `${NUMBER.format(count)} survey${count === 1 ? '' : 's'}`;
}

/** What the measuring pass decided. `ticks` is per bucket; missing means shown. */
interface Fit {
  ticks: readonly boolean[];
  valuesHidden: boolean;
  stacked: boolean;
  /**
   * Whether the first and last axis labels hug the plot's edges instead of
   * centring under their bars — only when a label is wider than its slot,
   * where centring would put half of "Jan" outside the plot. On the Yearly
   * axis, or a tablet's wide months, every label sits under its own bar.
   */
  hug: boolean;
  /**
   * The card width the pass measured at, printed as `data-measured-width` —
   * a test that has just resized the page waits for it to match before it
   * reads the layout, since the observer fires a frame later than the resize.
   */
  width: number;
}

const OPEN: Fit = { ticks: [], valuesHidden: false, stacked: false, hug: false, width: 0 };

function sameFit(a: Fit, b: Fit): boolean {
  return (
    a.width === b.width &&
    a.hug === b.hug &&
    a.valuesHidden === b.valuesHidden &&
    a.stacked === b.stacked &&
    a.ticks.length === b.ticks.length &&
    a.ticks.every((tick, index) => tick === b.ticks[index])
  );
}

let measuringContext: CanvasRenderingContext2D | null | undefined;

/**
 * The width `text` renders at in `element`'s font, off a canvas — the same
 * face, weight and size the element computes to, measured without the
 * element's own box being allowed to wrap or clip it. Zero when there is no
 * canvas to draw on (nothing then hides, which is the safe failure).
 */
function textWidth(element: Element, text: string): number {
  if (measuringContext === undefined) {
    measuringContext = document.createElement('canvas').getContext('2d');
  }
  if (!measuringContext) return 0;
  const style = getComputedStyle(element);
  measuringContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return measuringContext.measureText(text).width;
}

/**
 * A hand-drawn rising-line arrow with a head — one path serves both
 * directions: `down` is the same `up` group flipped vertically, not a second
 * path to keep in sync. `aria-hidden`: the reading beside it already says
 * "+" or "−".
 */
function TrendArrow({ direction }: { direction: 'up' | 'down' }): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`chart__arrow chart__arrow--${direction}`}
      aria-hidden="true"
      focusable="false"
    >
      <g transform={direction === 'down' ? 'scale(1 -1) translate(0 -16)' : undefined}>
        <path
          d="M2.5 11.5l4-4 3 3 4-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 5.5h3.5V9"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

export function SurveyChart({ records }: SurveyChartProps): ReactNode {
  const titleId = useId();
  const detailId = useId();
  const card = useRef<HTMLElement | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const [range, setRange] = useState<ChartRange>('monthly');
  const [fit, setFit] = useState<Fit>(OPEN);
  const [detailOpen, setDetailOpen] = useState(false);

  // Same reasoning as round 4: this flag, not a parent conditional, is what
  // decides whether the chart draws anything, so the effects below can depend
  // on it and still fire the first time `records` crosses the gate.
  const visible = records.length >= 2;

  const now = new Date();
  const buckets = range === 'monthly' ? surveysByMonth(records, now) : surveysByYear(records, now);
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  const change = periodChange(records, range, now);
  const currentIndex = buckets.findIndex((bucket) => bucket.current);
  const first = buckets[0]?.key ?? '';
  const last = buckets[buckets.length - 1]?.key ?? '';
  const span = range === 'monthly' ? String(now.getFullYear()) : first === last ? first : `${first}–${last}`;

  /* The measuring pass — before paint, and again whenever the card's box
   * changes (a rotation, a text setting) or the faces finish loading. */
  useLayoutEffect(() => {
    if (!visible) return;
    const node = card.current;
    if (!node) return;

    const measure = (): void => {
      const bars = node.querySelector<HTMLElement>('.chart__bars');
      const summary = node.querySelector<HTMLElement>('.chart__summary');
      const total = node.querySelector<HTMLElement>('.chart__total-n');
      const delta = node.querySelector<HTMLElement>('.chart__delta-text');
      if (!bars || !summary || !total || !delta) return;

      const slots = Array.from(node.querySelectorAll<HTMLElement>('.chart__slot'));
      const pitch = bars.clientWidth / Math.max(1, slots.length);
      const tickWidths = slots.map((slot) => {
        const label = slot.querySelector<HTMLElement>('.chart__x');
        return label ? textWidth(label, label.textContent ?? '') : 0;
      });
      const valueWidths = slots.flatMap((slot) => {
        const value = slot.querySelector<HTMLElement>('.chart__count');
        const text = value?.textContent ?? '';
        return value && text ? [textWidth(value, text)] : [];
      });
      const gap = Number.parseFloat(getComputedStyle(summary).columnGap) || 0;
      const inset =
        Number.parseFloat(getComputedStyle(node).getPropertyValue('--chart-inset')) || 0;

      const next: Fit = {
        width: node.clientWidth,
        ticks: visibleTicks(tickWidths, pitch, currentIndex),
        valuesHidden: !valuesFit(valueWidths, pitch),
        hug: Math.max(0, ...tickWidths) > pitch,
        stacked: summaryStacks({
          totalWidth: textWidth(total, total.textContent ?? ''),
          deltaWidth: textWidth(delta, delta.textContent ?? ''),
          summaryWidth: summary.clientWidth,
          gap,
          inset,
        }),
      };
      setFit((prev) => (sameFit(prev, next) ? prev : next));
    };

    measure();
    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(node);
    // A fallback face measures differently from Barlow; measure again once
    // the real one is in.
    void document.fonts.ready.then(measure);
    return () => {
      observer.disconnect();
    };
  }, [visible, range, records, currentIndex]);

  useEffect(() => {
    if (!visible) return;
    const node = stage.current;
    if (!node) return;

    const fills = Array.from(node.querySelectorAll<HTMLElement>('.chart__fill'));
    if (fills.length === 0) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      gsap.set(fills, { scaleY: 1 });
      return;
    }

    gsap.set(fills, { scaleY: 0 });
    gsap.to(fills, { scaleY: 1, duration: 0.5, ease: 'power2.out', stagger: 0.04 });
  }, [visible, range]);

  if (!visible) return null;

  const className = [
    'chart',
    fit.stacked ? 'chart--stacked' : '',
    fit.valuesHidden ? 'chart--values-hidden' : '',
    fit.hug ? 'chart--hug' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      className={className}
      ref={card}
      aria-labelledby={titleId}
      data-range={range}
      data-measured-width={fit.width}
    >
      <div className="chart__head">
        <h2 className="chart__title" id={titleId} data-legibility="body">
          Surveys created
        </h2>
        <Segmented
          options={RANGE_OPTIONS}
          value={range}
          onChange={(next) => {
            setRange(next);
            setDetailOpen(false);
          }}
          ariaLabel="Chart range"
        />
      </div>

      <div className="chart__summary">
        <div className="chart__total">
          <p className="chart__total-n" data-legibility="display" data-testid="chart-total">
            {NUMBER.format(records.length)}
          </p>
          <p className="chart__total-cap" data-legibility="body">
            Total on device
          </p>
        </div>
        <div
          className="chart__period"
          data-testid="chart-change"
          data-direction={change.direction}
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="chart__period-n" data-legibility="body" data-testid="chart-current">
            {change.currentLabel}
          </p>
          <p className="chart__delta" data-legibility="body">
            {change.direction !== 'flat' ? <TrendArrow direction={change.direction} /> : null}
            <span className="chart__delta-text" data-testid="chart-delta">
              {change.delta}
            </span>
          </p>
          <p className="chart__compare" data-legibility="meta" data-testid="chart-compare">
            {change.compare}
          </p>
        </div>
      </div>

      {/* Round 9.1 (D-044) item 4: her feedback — "Through Sep 7" repeated a
       * date already shown below the chart, so the line is gone; `span` alone
       * still names the range being plotted. */}
      <div className="chart__plot-head">
        <p className="chart__span" data-legibility="body" data-testid="chart-span">
          {span}
        </p>
      </div>

      <div
        className="chart__bars"
        ref={stage}
        key={range}
        role="group"
        aria-label={`Surveys created, ${range === 'monthly' ? 'by month this year' : 'by year'}`}
      >
        {buckets.map((bucket, index) => {
          const tickHidden = fit.ticks[index] === false;
          return (
            <div
              className={`chart__slot${bucket.current ? ' chart__slot--current' : ''}`}
              key={bucket.key}
              role="img"
              aria-label={`${countWord(bucket.count)} — ${bucket.label}`}
            >
              <span className="chart__count" data-legibility="meta" aria-hidden="true">
                {bucket.count > 0 ? NUMBER.format(bucket.count) : ''}
              </span>
              <span className="chart__track">
                {bucket.count > 0 ? (
                  <span
                    className="chart__fill"
                    style={{ height: `${String((bucket.count / max) * 100)}%` }}
                  />
                ) : null}
              </span>
              <span
                className={`chart__x${tickHidden ? ' chart__x--hidden' : ''}`}
                data-legibility="meta"
                aria-hidden="true"
              >
                {bucket.short}
              </span>
            </div>
          );
        })}
      </div>

      {fit.valuesHidden ? (
        <div className="chart__detail">
          <button
            type="button"
            className="chart__detail-btn"
            aria-expanded={detailOpen}
            aria-controls={detailId}
            data-legibility="body"
            onClick={() => {
              setDetailOpen((open) => !open);
            }}
          >
            {detailOpen ? 'Hide counts' : 'Show counts'}
          </button>
          {detailOpen ? (
            <ul className="chart__detail-list" id={detailId}>
              {buckets.map((bucket) => (
                <li
                  key={bucket.key}
                  className={`chart__detail-item${bucket.current ? ' chart__detail-item--current' : ''}`}
                  data-legibility="body"
                >
                  <span className="chart__detail-label">{bucket.short}</span>
                  <span className="chart__detail-n">{NUMBER.format(bucket.count)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
